import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { addDays, format } from 'date-fns';
import { apiErrorMessage, appointmentsService, shopsService } from '../services/api';
import { AppointmentTracking, ShopSettings } from '../types';
import {
  formatClockTime,
  shopToday,
  shopWallClockToEpoch,
  shopZoneAbbreviation,
  toDateKey,
} from '../utils/shopTime';
import { findNextOpenDate, isDateOpen } from '../utils/shopAvailability';

interface ManageBookingCardProps {
  appointment: AppointmentTracking;
  /** From the `t` query param on the emailed tracking link. */
  token: string;
  shopId?: string;
  onChanged: (updated: AppointmentTracking) => void;
}

/**
 * Lets a customer move or cancel their own booking, straight from the tracking
 * link. Only rendered when the link carries a token — the appointment id alone
 * is enough to *read* this page, not to change it.
 *
 * Slots come from the same public endpoint the booking form uses, so what is
 * offered here is exactly what the API will accept.
 */
const ManageBookingCard: React.FC<ManageBookingCardProps> = ({
  appointment,
  token,
  shopId,
  onChanged,
}) => {
  const [rescheduling, setRescheduling] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [settings, setSettings] = useState<ShopSettings | null>(null);
  const [date, setDate] = useState<Date | null>(null);
  const [slots, setSlots] = useState<string[]>([]);
  const [slot, setSlot] = useState<string | null>(null);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // The shop's clock comes with the tracking view: this page is anonymous and
  // cannot read shop settings, which used to leave the visitor's own clock to
  // decide "tomorrow" and the instant a new slot meant.
  const timeZone = appointment.timezone || settings?.timezone;

  // Shop settings drive which days the picker offers; without them every day
  // is offered and the slots endpoint does the real filtering.
  useEffect(() => {
    if (!rescheduling || !shopId) return;

    let cancelled = false;
    shopsService
      .getShopSettings(shopId)
      .then((loaded) => {
        if (cancelled) return;
        setSettings(loaded);
        setDate(findNextOpenDate(addDays(shopToday(timeZone), 1), loaded));
      })
      .catch(() => {
        if (cancelled) return;
        setDate(addDays(shopToday(timeZone), 1));
      });

    return () => {
      cancelled = true;
    };
  }, [rescheduling, shopId, timeZone]);

  useEffect(() => {
    if (!rescheduling || !shopId || !date) return;

    let cancelled = false;
    setLoadingSlots(true);
    setSlot(null);

    appointmentsService
      .getAvailableTimeSlots(shopId, toDateKey(date))
      .then((available) => {
        if (!cancelled) setSlots(available);
      })
      .catch(() => {
        if (!cancelled) setSlots([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingSlots(false);
      });

    return () => {
      cancelled = true;
    };
  }, [rescheduling, shopId, date]);

  const shouldDisableDate = useCallback(
    (candidate: Date) => !isDateOpen(candidate, settings),
    [settings]
  );

  const canSubmit = useMemo(
    () => Boolean(date && slot && !busy),
    [date, slot, busy]
  );

  const handleReschedule = async () => {
    if (!date || !slot) return;

    setBusy(true);
    setError(null);

    try {
      const when = shopWallClockToEpoch(toDateKey(date), slot, timeZone);
      onChanged(
        await appointmentsService.rescheduleAsCustomer(appointment.id, token, when)
      );
      setRescheduling(false);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not move your appointment. Try another time.'));
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    setBusy(true);
    setError(null);

    try {
      onChanged(await appointmentsService.cancelAsCustomer(appointment.id, token));
      setConfirmingCancel(false);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not cancel your appointment.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Need to make a change?
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          You can move or cancel this appointment up to two hours before it starts.
          After that, please call the shop.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {!rescheduling ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Button variant="outlined" onClick={() => setRescheduling(true)}>
              Change the time
            </Button>
            <Button color="error" onClick={() => setConfirmingCancel(true)}>
              Cancel appointment
            </Button>
          </Stack>
        ) : (
          <Stack spacing={2.5}>
            <DatePicker
              label="New date"
              value={date}
              onChange={setDate}
              minDate={shopToday(timeZone)}
              shouldDisableDate={shouldDisableDate}
              slotProps={{ textField: { fullWidth: true } }}
            />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Available times{date ? ` on ${format(date, 'EEEE, MMMM d')}` : ''}
              </Typography>

              {loadingSlots ? (
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ py: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    Checking the shop&apos;s schedule…
                  </Typography>
                </Stack>
              ) : slots.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nothing free that day. Try another date.
                </Typography>
              ) : (
                <Stack direction="row" flexWrap="wrap" useFlexGap gap={1}>
                  {slots.map((available) => (
                    <Chip
                      key={available}
                      label={formatClockTime(available)}
                      onClick={() => setSlot(available)}
                      color={slot === available ? 'primary' : 'default'}
                      variant={slot === available ? 'filled' : 'outlined'}
                    />
                  ))}
                </Stack>
              )}
              {timeZone && slots.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Times are the shop&apos;s local time ({shopZoneAbbreviation(timeZone)}).
                </Typography>
              )}
            </Box>

            <Stack direction="row" spacing={1.5}>
              <Button variant="contained" onClick={handleReschedule} disabled={!canSubmit}>
                {busy ? 'Moving…' : 'Confirm new time'}
              </Button>
              <Button onClick={() => setRescheduling(false)} disabled={busy}>
                Never mind
              </Button>
            </Stack>
          </Stack>
        )}
      </CardContent>

      <Dialog open={confirmingCancel} onClose={() => setConfirmingCancel(false)}>
        <DialogTitle>Cancel this appointment?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The shop will be told straight away. You will need to book again if you
            change your mind.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmingCancel(false)} disabled={busy}>
            Keep it
          </Button>
          <Button color="error" variant="contained" onClick={handleCancel} disabled={busy}>
            {busy ? 'Canceling…' : 'Cancel appointment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default ManageBookingCard;
